import { useCallback, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "utils/api";
import { toFolderSuggestionError } from "../utils";
import type { FloorPlanFolderSuggestion } from "../../utils/types";
import type { UseFloorPlanFolderSuggestion } from "./types";

export const floorPlanFolderSuggestionQueryKey = (projectId: string) => [
  "quantity-surveyor-floor-plan-folder-suggestion",
  projectId,
];

export const useFloorPlanFolderSuggestion: UseFloorPlanFolderSuggestion = ({
  projectId,
}) => {
  const queryClient = useQueryClient();

  const suggestionQuery = useQuery({
    queryKey: floorPlanFolderSuggestionQueryKey(projectId),
    queryFn: async ({ signal }) => {
      const suggestion: FloorPlanFolderSuggestion =
        await api.suggestQuantitySurveyorFloorPlanFolders(projectId, signal);
      return suggestion;
    },
    // Runs only when the user asks; every run spends model calls
    enabled: false,
    // The result lives as long as the modal; closing it drops the cache
    gcTime: 0,
    retry: false,
  });

  const { refetch } = suggestionQuery;

  // A request while one runs joins it instead of restarting it
  const requestSuggestion = useCallback(async () => {
    await refetch({ cancelRefetch: false });
  }, [refetch]);

  // Aborts a running request (the backend stops the model calls on the disconnect) and drops the result
  const clearSuggestion = useCallback(async () => {
    const queryKey = floorPlanFolderSuggestionQueryKey(projectId);
    await queryClient.cancelQueries({ queryKey });
    await queryClient.resetQueries({ queryKey });
  }, [queryClient, projectId]);

  const suggestError = useMemo(
    () => toFolderSuggestionError({ error: suggestionQuery.error }),
    [suggestionQuery.error],
  );

  return {
    suggestion: suggestionQuery.data ?? null,
    suggestionUpdatedAt: suggestionQuery.dataUpdatedAt,
    isSuggesting: suggestionQuery.isFetching,
    suggestError,
    requestSuggestion,
    clearSuggestion,
  };
};
